import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import StepWizard from 'react-step-wizard';
import { useDispatch, useSelector } from 'react-redux';
import { quizFormCurrentStep } from '@redux/quiz/actions';

import Container from '@common/Container';
import Section from '@common/Section';
import Row from '@common/Row';
import RichText from '@common/RichText';
import SmallTitle from '@common/SmallTitle';
import MarketoForm from '@common/MarketoForm';
import Img from '@common/Image';

import Video from '@common/blocks/flex/Video';
import Buttons from '@common/blocks/flex/Buttons';
import Links from '@common/blocks/flex/Links';
import MobileLinks from '@common/blocks/flex/MobileLinks';

import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import StepFive from './StepFive';

import * as styles from './homepage-quiz.module.scss';

const HomepageQuiz = ({ data }) => {
  const {
    title,
    titleAlignment,
    showTitle,
    text,
    textAlignment,
    textFontSize,
    textFontFamily,
    textColWidth,
    listClass,
    showImages,
    imagePosition,
    imagePositionTop,
    imagePositionTopLg,
    imageWidth,
    imageMaxHeight,
    imageNegativeMargin,
    buttons,
    images,
    links,
    video,
    topMargin,
    bottomMargin,
    bgColour,
    bgNoPadding,
    bgFullwidth
  } = data;
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const quiz = useSelector(state => state.quiz);
  const [prefill, setPrefill] = useState(null);
  const [leadSent, setLeadSent] = useState(false);
  const [resultsSent, setResultsSent] = useState(false);
  useEffect(() => {
    if (quiz.submitLead && !leadSent) {
      setPrefill({
        FirstName: user.firstName,
        LastName: user.lastName,
        Email: user.email,
        Title: user.title,
        Phone: user.phone,
        prodQuizGoals1TeacherEngagement: quiz.recommend.findIndex(x => x === 'prodQuizGoals1TeacherEngagement') !== -1,
        prodQuizGoals2ParentComms: quiz.recommend.findIndex(x => x === 'prodQuizGoals2ParentComms') !== -1,
        prodQuizGoals3Admin: quiz.recommend.findIndex(x => x === 'prodQuizGoals3Admin') !== -1,
        prodQuizGoals4TeacherSkills: quiz.recommend.findIndex(x => x === 'prodQuizGoals4TeacherSkills') !== -1,
        prodQuizGoals5Activities: quiz.recommend.findIndex(x => x === 'prodQuizGoals5Activities') !== -1,
        prodQuizGoals6LessonPlanning: quiz.recommend.findIndex(x => x === 'prodQuizGoals6LessonPlanning') !== -1,
        prodQuizGoals7Attendance: quiz.recommend.findIndex(x => x === 'prodQuizGoals7Attendance') !== -1,
        prodQuizGoals8Insights: quiz.recommend.findIndex(x => x === 'prodQuizGoals8Insights') !== -1
      });
      setLeadSent(true);
    }
    if (quiz.submitResults && !resultsSent) {
      // Converts string to boolean
      const currentSolution = user.currentSolution == 'true';
      setPrefill({
        Email: user.email,
        Company: user.centerName,
        stateProvince: user.stateProvince,
        prodQuizCurrentlyUsingAnotherPlatform: currentSolution,
        prodQuizResult: quiz.result,
        Enrollment__c: user.enrollmentRange
      });
      setResultsSent(true);
    }
  }, [user, quiz]);
  useEffect(() => {
    dispatch(quizFormCurrentStep(1));
  }, []);
  return (
    <Section style={{ marginTop: `calc(${imageNegativeMargin} + 50px)` }} spacing={clsx(topMargin, bottomMargin ? bottomMargin : 'mb-7')} className={clsx({
        [`bg-${bgColour}`]: bgColour,
        'py-5': bgColour && !bgNoPadding,
        'container card': !bgFullwidth
      })}>
      <Container>
        {video &&
          <Video data={data}/>
        }
        <Row className={clsx(`d-flex flex-column justify-content-center overflow-hidden`, {
            'flex-lg-row-reverse': imagePosition === 'Left' && !imagePositionTopLg,
            'flex-lg-row': imagePosition === 'Right' && !imagePositionTopLg,
            'flex-column-reverse': imagePositionTop || imagePositionTopLg,
            'justify-content-center': (!images || !links) && textAlignment === 'Center'
          })}>
          <div className={clsx('col', {
              [textColWidth]:quiz.step < 4,
              'ps-3': imagePosition === 'Left',
              'pe-3': imagePosition === 'Right',
              'text-center': textAlignment === 'Center',
              'text-end': textAlignment === 'Right',
              'text-start': textAlignment === 'Left',
            })}>
            {(showTitle && title) &&
              <SmallTitle>{title}</SmallTitle>
            }
            {quiz.step < 4 &&
              <div className='d-flex justify-content-center mb-3'>
                <div className={clsx('d-flex justify-content-between position-relative', styles.progress)}>
                  <div style={{height: 18, width: 18, borderRadius: '50%'}} className={clsx('border border-black border-2', quiz.step === 1 ? 'bg-yellow':'bg-white')}></div>
                  <div style={{height: 18, width: 18, borderRadius: '50%'}} className={clsx('border border-black border-2', quiz.step === 2 ? 'bg-yellow':'bg-white')}></div>
                  <div style={{height: 18, width: 18, borderRadius: '50%'}} className={clsx('border border-black border-2', quiz.step === 3 ? 'bg-yellow':'bg-white')}></div>
                </div>
              </div>
            }
            {/* Quiz */}
            <StepWizard>
              <StepOne />
              <StepTwo />
              <StepThree />
              <StepFour />
              <StepFive />
            </StepWizard>
            {quiz.submitLead &&
              <div className='sr-only' aria-hidden>
                <MarketoForm formId={'2308'} prefill={prefill} redirect={quiz.redirectUrl} submit={quiz.submitLead} noRefresh/>
              </div>
            }
            {/* Submit final results */}
            {quiz.submitResults &&
              <div className='sr-only' aria-hidden>
                <MarketoForm formId={'2309'} prefill={prefill} redirect={quiz.redirectUrl} submit={quiz.submitResults} noRefresh/>
              </div>
            }
            {text &&
              <RichText
                json={text}
                h1='mb-3'
                h2='mb-3'
                p={clsx('mb-3', textFontSize, textFontFamily)}
                a='fs-16'
                ul={listClass}
              />
            }
            {buttons && 
              <Buttons data={data} />
            }
          </div>
          {quiz.step < 4 && images &&
            <div className={clsx('col text-center d-flex justify-content-center', imageWidth, {
              [`d-none ${showImages}`]: showImages
              })}>
              <div className='d-flex align-self-center'>
                {images &&
                  <Img image={images[quiz.recommend.length].gatsbyImageData} file={images[quiz.recommend.length].file} loading='eager'/>
                }
              </div>
            </div>
          }
          {(links && quiz.step < 4) &&
            <Links data={data} />
          }
        </Row>
        {links &&
          <MobileLinks data={data} />
        }
      </Container>
    </Section>
  )
}

export default HomepageQuiz
